'use client';

import * as React from 'react';
import classNames from 'classnames';
import LoadingContent from '../LoadingContent';
import Icon, { IconsAvailable } from '../Icon';
import styles from './styles.module.scss';
import { Portal } from '@/components/ui/Portal';
import { useKeypress } from '@/components/hooks/useKeyPress';
import { Modifier, usePopper } from 'react-popper';
import MenuItem from '@/components/ui/MenuItem';
import Menu from '@/components/ui/Menu';
import Highlighter from '@/components/ui/Highlighter';

export type ControlInputTheme = 'dark' | 'light';

export interface IControlInputProps {
  /** Theme */
  theme: ControlInputTheme;
  /** Input type */
  type?: React.HTMLInputTypeAttribute;
  /** placeholder */
  placeholder?: string;
  /** Controlled value */
  value?: string;
  /** Default value */
  defaultValue?: string;
  /** Disabled status */
  disabled?: boolean;
  /** Readonly status */
  readonly?: boolean;
  /** Focus handler */
  onFocus?: React.DOMAttributes<HTMLInputElement>['onFocus'];
  /** Blur handler */
  onBlur?: React.DOMAttributes<HTMLInputElement>['onBlur'];
  /** Change handler */
  onChange?: (e: { target: { value: string } }) => void;
  /** Keydown handler */
  onKeyDown?: React.InputHTMLAttributes<HTMLInputElement>['onKeyDown'];
  /** Keyup handler */
  onKeyUp?: React.InputHTMLAttributes<HTMLInputElement>['onKeyUp'];
  /** Keyup handler */
  onClick?: React.InputHTMLAttributes<HTMLInputElement>['onClick'];
  /** Focus field on mount */
  autoFocus?: boolean;
  /** Addon element to append to field */
  addOn?: React.ReactNode;
  /** Addon element to prepend to field */
  addOnFront?: React.ReactNode;
  /** Color higlight for errors */
  hasError?: boolean;
  /** Autocomplete flag */
  autocomplete?: boolean;
  /** className to append */
  className?: string;
  /** Pass input ref */
  inputRef?: React.RefCallback<HTMLInputElement>;
  /** Styles to apply to wrapper */
  style?: React.CSSProperties;
  /** Styles to apply to inner input field */
  inputStyle?: React.CSSProperties;
  /** Max character count */
  maxLength?: number;
  /** DOM target element name attribute */
  name?: string;
  /** DOM target element id attribute */
  id?: string;
  /** Loading */
  loading?: boolean;
  /** Icon to display in front of everything */
  icon?: IconsAvailable;
  /** Title when used without associated label */
  title?: string;
  /** Menu */
  menu?: {
    data: {
      label: string;
      id: string;
    }[];
    onSelect: (item: string) => void;
  };
  /** Allow clearing */
  onClear?: () => void;
}

const sameWidth: Modifier<unknown> = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper.minWidth = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    const w = state.elements.reference.getBoundingClientRect();
    state.elements.popper.style.minWidth = `${w.width}px`;
  },
};

/**
 * Regular HTML input field with extended functionality and styling. Use this within form row.
 */
const ControlInput: React.FunctionComponent<IControlInputProps> = (props) => {
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = React.useState(-1);
  const [hasFocus, setHasFocus] = React.useState(false);
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [popperElement, setPopperElement] = React.useState<HTMLSpanElement | null>(null);

  const indexUp = React.useCallback(() => {
    if (props.menu && props.menu.data.length > 0) {
      if (selectedMenuItem > 0) {
        setSelectedMenuItem(selectedMenuItem - 1);
      }
    }
  }, [props.menu, selectedMenuItem]);

  const indexDown = React.useCallback(() => {
    if (props.menu && props.menu.data.length > 0) {
      if (selectedMenuItem + 1 < props.menu.data.length) {
        setSelectedMenuItem(selectedMenuItem + 1);
      }
    }
  }, [props.menu, selectedMenuItem]);

  useKeypress(
    'Escape',
    'keyup',
    () => {
      if (menuVisible) {
        setMenuVisible(false);
        setSelectedMenuItem(-1);
      }
    },
    [menuVisible],
  );

  useKeypress(
    'Enter',
    'keyup',
    () => {
      if (menuVisible && selectedMenuItem > -1 && props.menu && props.menu.data.length > 0 && props.menu.data[selectedMenuItem]) {
        props.menu.onSelect(props.menu.data[selectedMenuItem].id);
      }
    },
    [menuVisible, selectedMenuItem],
  );

  useKeypress(
    'ArrowUp',
    'keydown',
    (e) => {
      if (menuVisible) {
        e.preventDefault();
        indexUp();
      } else if (hasFocus && !menuVisible && props.menu && props.menu.data.length > 0) {
        e.preventDefault();
        setSelectedMenuItem(-1);
        setMenuVisible(true);
      }
    },
    [menuVisible, indexUp],
  );

  useKeypress(
    'ArrowDown',
    'keydown',
    (e) => {
      if (menuVisible) {
        e.preventDefault();
        indexDown();
      } else if (hasFocus && !menuVisible && props.menu && props.menu.data.length > 0) {
        e.preventDefault();
        setSelectedMenuItem(-1);
        setMenuVisible(true);
      }
    },
    [menuVisible, indexDown],
  );

  const testForFocus = React.useCallback(
    (target: Node) => {
      if (target && popperElement && popperElement.contains(target as Node)) {
        if (inputRef.current && !hasFocus) {
          inputRef.current.focus();
        }
        setHasFocus(true);
        setMenuVisible(true);
        return;
      }
      if (target && wrapperRef.current && !wrapperRef.current.contains(target as Node)) {
        setHasFocus(false);
        setMenuVisible(false);
        setSelectedMenuItem(-1);
        return;
      }
    },
    [hasFocus, popperElement],
  );

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      testForFocus(target as Node);
    };

    globalThis.addEventListener('click', handleClickOutside);
    return () => {
      globalThis.removeEventListener('click', handleClickOutside);
    };
  }, [menuVisible, wrapperRef, popperElement, hasFocus, testForFocus]);

  React.useEffect(() => {
    setSelectedMenuItem(-1);
  }, [props.menu?.data]);

  const { styles: popperStyles, attributes } = usePopper(wrapperRef.current, popperElement, {
    strategy: 'fixed',
    placement: 'bottom',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          padding: 20,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
      sameWidth,
    ],
  });

  const ariaId = 'aria-' + React.useId();

  return (
    <div
      className={classNames(
        styles['control-input'],
        {
          [styles['has-error']]: props.hasError,
          [styles['has-value']]: props.value && props.value.length > 0,
          [styles['focus']]: hasFocus,
          [styles['disabled']]: props.disabled,
          [styles['readonly']]: props.readonly,
          [styles['theme-light']]: props.theme === 'light',
          [styles['theme-dark']]: props.theme === 'dark',
        },
        props.className,
      )}
      style={props.style}
      onClick={(e) => {
        e.preventDefault();
        if (inputRef.current && !hasFocus) {
          inputRef.current.focus();
        }
      }}
      role="button"
      ref={wrapperRef}
    >
      {props.icon && (
        <Icon
          width={16}
          height={16}
          kind={props.icon}
          wrapperClassName={styles['control-input__icon']}
        />
      )}
      {props.addOnFront && <span className={styles['control-input__addon-front']}>{props.addOnFront}</span>}
      <div className={styles['control-input__holder']}>
        <input
          style={props.inputStyle}
          maxLength={props.maxLength}
          className={styles['control-input__input']}
          type={passwordVisible ? 'text' : props.type}
          value={props.value}
          defaultValue={props.defaultValue}
          disabled={props.disabled}
          readOnly={props.readonly}
          onFocus={(e) => {
            setHasFocus(true);
            if (props.menu && props.menu.data.length > 0 && !menuVisible) {
              setMenuVisible(true);
            }
            if (props.onFocus) {
              props.onFocus(e);
            }
          }}
          onBlur={(e) => {
            setTimeout(() => {
              testForFocus(document.activeElement as Node);
            }, 0);
            if (props.onBlur) {
              props.onBlur(e);
            }
          }}
          onChange={props.onChange}
          onKeyDown={props.onKeyDown}
          onKeyUp={props.onKeyUp}
          onClick={props.onClick}
          placeholder={props.placeholder}
          spellCheck={false}
          ref={(e) => {
            if (props.inputRef) {
              props.inputRef(e);
            }
            if (e) {
              inputRef.current = e;
              if (props.autoFocus) {
                window.requestAnimationFrame(() => {
                  e.focus();
                });
              }
            }
          }}
          autoComplete={props.autocomplete ? 'on' : 'off'}
          name={props.name}
          id={props.id}
          title={props.title}
          aria-busy={props.loading}
        />
      </div>
      {props.loading && (
        <span className={styles['control-input__loader']}>
          <LoadingContent size="tiny" />
        </span>
      )}
      {props.addOn && <span className={styles['control-input__addon']}>{props.addOn}</span>}
      {props.onClear && (
        <span className={styles['control-input__clear']}>
          <button
            type="button"
            disabled={props.value === ''}
            onClick={() => {
              if (props.onClear) {
                props.onClear();
              }
              inputRef.current?.focus();
            }}
            onFocus={() => {
              setHasFocus(true);
            }}
            onBlur={() => {
              testForFocus(document.activeElement as Node);
            }}
          >
            <Icon
              width={16}
              height={16}
              kind="clear"
            />
          </button>
        </span>
      )}
      {props.type === 'password' && (
        <span className={classNames(styles['control-input__toggle'], { [styles['active']]: passwordVisible })}>
          <button
            type="button"
            onClick={() => {
              setPasswordVisible(!passwordVisible);
            }}
          >
            <Icon
              width={16}
              height={16}
              kind={passwordVisible ? 'password-show' : 'password-hide'}
            />
          </button>
        </span>
      )}
      {props.menu && props.menu.data.length > 0 && menuVisible && (
        <Portal>
          <div
            className={styles['control-input__menu']}
            ref={setPopperElement}
            style={popperStyles.popper}
            role="menu"
            aria-labelledby={props.id || ariaId}
            id={`${props.id || ariaId}-menu`}
            tabIndex={-1}
            {...attributes.popper}
          >
            <Menu>
              {props.menu.data.map((item, itemIndex) => (
                <MenuItem
                  title={
                    <Highlighter
                      text={item.label}
                      search={(props.value || '').trim()}
                    />
                  }
                  key={item.id}
                  active={itemIndex === selectedMenuItem}
                  onMouseEnter={() => {
                    inputRef.current?.focus();
                  }}
                  onClick={() => {
                    if (props.menu) {
                      // inputRef.current?.focus();
                      setHasFocus(false);
                      setMenuVisible(false);
                      props.menu.onSelect(item.id);
                    }
                  }}
                />
              ))}
            </Menu>
          </div>
        </Portal>
      )}
    </div>
  );
};

ControlInput.displayName = 'ControlInput';

export default ControlInput;
